import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import {
  Palette, Plus, Trash2, ExternalLink, Loader2, Check,
  AlertCircle, ToggleLeft, ToggleRight, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import { products } from '../../data/products';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CanvaTemplateManagement = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSlug, setExpandedSlug] = useState(null);
  const [editingSlug, setEditingSlug] = useState(null);
  const [editVariants, setEditVariants] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/canva-templates`);
      setTemplates(res.data.templates || []);
    } catch (err) {
      console.error('Failed to fetch Canva templates:', err);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const templateMap = {};
  templates.forEach(t => { templateMap[t.product_slug] = t; });

  const startEditing = (slug) => {
    const existing = templateMap[slug];
    if (existing) {
      setEditVariants(existing.variants.map(v => ({ ...v })));
    } else {
      setEditVariants([{ id: '', name: 'Standard', canva_url: '', is_default: true }]);
    }
    setEditingSlug(slug);
    setExpandedSlug(slug);
  };

  const addVariant = () => {
    setEditVariants(prev => [...prev, {
      id: '', name: `Variant ${prev.length + 1}`, canva_url: '', is_default: false
    }]);
  };

  const updateVariant = (idx, field, value) => {
    setEditVariants(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      if (field === 'is_default' && value) {
        next.forEach((v, i) => { if (i !== idx) v.is_default = false; });
      }
      return next;
    });
  };

  const removeVariant = (idx) => {
    setEditVariants(prev => {
      const next = prev.filter((_, i) => i !== idx);
      if (next.length > 0 && !next.some(v => v.is_default)) next[0].is_default = true;
      return next;
    });
  };

  const saveTemplate = async (slug) => {
    const validVariants = editVariants.filter(v => v.canva_url.trim());
    if (validVariants.length === 0) {
      toast.error('Add at least one Canva link');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/canva-templates/${slug}`, {
        product_slug: slug,
        enabled: true,
        variants: validVariants.map(v => ({
          name: v.name, canva_url: v.canva_url, is_default: v.is_default
        })),
      });
      toast.success('Canva template saved');
      setEditingSlug(null);
      fetchTemplates();
    } catch (err) {
      toast.error('Could not save: ' + (err.response?.data?.detail || err.message));
    } finally { setSaving(false); }
  };

  const toggleTemplate = async (slug) => {
    try {
      const res = await axios.patch(`${API}/admin/canva-templates/${slug}/toggle`);
      toast.success(res.data.enabled ? 'Canva enabled' : 'Canva disabled');
      fetchTemplates();
    } catch (err) {
      toast.error('Toggle failed');
    }
  };

  const deleteTemplate = async (slug) => {
    if (!window.confirm(`Delete Canva template for ${slug}?`)) return;
    try {
      await axios.delete(`${API}/admin/canva-templates/${slug}`);
      toast.success('Canva template deleted');
      fetchTemplates();
    } catch (err) {
      toast.error('Could not delete');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
      </div>
    );
  }

  const configuredCount = templates.length;
  const enabledCount = templates.filter(t => t.enabled).length;

  return (
    <div data-testid="canva-management">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-sans font-bold text-stone-800 text-lg flex items-center gap-2">
            <Palette className="w-5 h-5 text-[#00C4CC]" /> Canva Template Management
          </h3>
          <p className="text-xs font-sans text-stone-500 mt-1">
            {configuredCount} products configured · {enabledCount} active
          </p>
        </div>
      </div>

      {/* Product List */}
      <div className="space-y-2">
        {products.map(product => {
          const tmpl = templateMap[product.slug];
          const isExpanded = expandedSlug === product.slug;
          const isEditing = editingSlug === product.slug;
          const hasCanva = !!tmpl;
          const isEnabled = tmpl?.enabled !== false;
          const variantCount = tmpl?.variants?.length || 0;

          return (
            <div key={product.slug}
              data-testid={`canva-product-${product.slug}`}
              className={`border rounded-xl overflow-hidden transition-all ${
                hasCanva ? (isEnabled ? 'border-[#00C4CC]/30 bg-[#00C4CC]/[0.02]' : 'border-stone-200 bg-stone-50/50') : 'border-stone-100 bg-white'
              }`}
            >
              {/* Product Row */}
              <div className="flex items-center gap-3 px-4 py-3 cursor-pointer"
                onClick={() => isEditing ? null : setExpandedSlug(isExpanded ? null : product.slug)}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-sans font-semibold text-stone-800 text-sm truncate">{product.title}</span>
                    {hasCanva && isEnabled && (
                      <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#00C4CC]/10 text-[#00C4CC]">
                        {variantCount} {variantCount === 1 ? 'template' : 'templates'}
                      </span>
                    )}
                    {hasCanva && !isEnabled && (
                      <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-stone-100 text-stone-400">
                        Disabled
                      </span>
                    )}
                  </div>
                  <span className="text-[10px] text-stone-400 font-sans">{product.slug}</span>
                </div>
                <div className="flex items-center gap-2">
                  {!isEditing && (
                    <Button
                      data-testid={`canva-edit-${product.slug}`}
                      variant="ghost"
                      size="sm"
                      className="h-8 px-3 text-xs font-sans"
                      onClick={(e) => { e.stopPropagation(); startEditing(product.slug); }}
                    >
                      {hasCanva ? 'Edit' : <><Plus className="w-3 h-3 mr-1" /> Add</>}
                    </Button>
                  )}
                  {hasCanva && !isEditing && (
                    <>
                      <button
                        data-testid={`canva-toggle-${product.slug}`}
                        onClick={(e) => { e.stopPropagation(); toggleTemplate(product.slug); }}
                        className="text-stone-400 hover:text-stone-600"
                        title={isEnabled ? 'Disable' : 'Enable'}
                      >
                        {isEnabled
                          ? <ToggleRight className="w-5 h-5 text-[#00C4CC]" />
                          : <ToggleLeft className="w-5 h-5" />}
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteTemplate(product.slug); }}
                        className="text-stone-300 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  {!isEditing && (isExpanded ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />)}
                </div>
              </div>

              {/* Expanded: View or Edit */}
              {isExpanded && (
                <div className="border-t border-stone-100 px-4 py-4">
                  {isEditing ? (
                    <div data-testid={`canva-editor-${product.slug}`}>
                      <div className="space-y-3 mb-4">
                        {editVariants.map((variant, idx) => (
                          <div key={idx} className="bg-white border border-stone-100 rounded-lg p-3 space-y-2">
                            <div className="flex items-center gap-2">
                              <input
                                data-testid={`canva-variant-name-${idx}`}
                                type="text"
                                value={variant.name}
                                onChange={(e) => updateVariant(idx, 'name', e.target.value)}
                                placeholder="Variant name (e.g. Minimal, Elegant)"
                                className="flex-1 px-3 py-2 border border-stone-200 rounded-lg text-xs font-sans focus:outline-none focus:border-[#00C4CC]"
                              />
                              <label className="flex items-center gap-1.5 text-[10px] font-sans text-stone-500 cursor-pointer whitespace-nowrap">
                                <input
                                  type="checkbox"
                                  checked={variant.is_default}
                                  onChange={() => updateVariant(idx, 'is_default', true)}
                                  className="accent-[#00C4CC]"
                                />
                                Default
                              </label>
                              {editVariants.length > 1 && (
                                <button onClick={() => removeVariant(idx)} className="text-stone-300 hover:text-red-500">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                            <input
                              data-testid={`canva-variant-url-${idx}`}
                              type="url"
                              value={variant.canva_url}
                              onChange={(e) => updateVariant(idx, 'canva_url', e.target.value)}
                              placeholder="https://www.canva.com/design/DAGxxx/view..."
                              className="w-full px-3 py-2 border border-stone-200 rounded-lg text-xs font-sans text-stone-600 focus:outline-none focus:border-[#00C4CC] placeholder:text-stone-300"
                            />
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={addVariant}
                          className="text-xs font-sans text-[#00C4CC] hover:text-[#00C4CC]/80 h-8">
                          <Plus className="w-3 h-3 mr-1" /> Add variant
                        </Button>
                        <div className="flex-1" />
                        <Button variant="ghost" size="sm" onClick={() => setEditingSlug(null)}
                          className="text-xs font-sans h-8">
                          Cancel
                        </Button>
                        <Button size="sm" onClick={() => saveTemplate(product.slug)}
                          disabled={saving}
                          data-testid={`canva-save-${product.slug}`}
                          className="bg-[#00C4CC] text-white hover:bg-[#00C4CC]/90 text-xs font-sans h-8 px-4">
                          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Check className="w-3 h-3 mr-1" /> Save</>}
                        </Button>
                      </div>
                    </div>
                  ) : tmpl ? (
                    <div className="space-y-2">
                      {tmpl.variants.map((v, i) => (
                        <div key={v.id} className="flex items-center gap-3 bg-white/60 rounded-lg px-3 py-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-sans font-semibold text-stone-700">{v.name}</span>
                              {v.is_default && (
                                <span className="text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#00C4CC]/10 text-[#00C4CC]">Default</span>
                              )}
                            </div>
                            <p className="text-[10px] text-stone-400 font-sans truncate mt-0.5">{v.canva_url}</p>
                          </div>
                          <a href={v.canva_url} target="_blank" rel="noopener noreferrer"
                            className="text-stone-400 hover:text-[#00C4CC] transition-colors"
                            title="Open in Canva">
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        </div>
                      ))}
                      <p className="text-[10px] text-stone-400 font-sans mt-2">
                        Last updated: {tmpl.updated_at ? new Date(tmpl.updated_at).toLocaleDateString('en-GB') : '—'}
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs font-sans text-stone-400 text-center py-4">
                      No Canva template configured. Click "Add" to get started.
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Help section */}
      <div className="mt-6 bg-stone-50 rounded-xl p-4 border border-stone-100">
        <h4 className="text-xs font-sans font-bold text-stone-600 mb-2">How to get a Canva Template Link</h4>
        <ol className="text-[11px] font-sans text-stone-500 space-y-1 list-decimal list-inside">
          <li>Open your design in Canva</li>
          <li>Click <strong>Share</strong> in the top right</li>
          <li>Select <strong>More...</strong></li>
          <li>Click <strong>Template link</strong></li>
          <li>Copy the URL and paste it here</li>
        </ol>
        <p className="text-[10px] font-sans text-stone-400 mt-2">
          When a customer clicks the link, an editable copy is created in their Canva account. Works with Canva Free.
        </p>
      </div>
    </div>
  );
};
